import gql from "graphql-tag";

const SIZE_CHART_FRAGMENT = gql`
	fragment SizeChartFragment on SizeChart {
		name
		description
		includeLengths
		sizes {
			lengths {
				length
				sizeId
				sizeLengthId
				isRetired
			}
			name
			showSize
			sizeId
			isRetired
		}
	}
`;

const COLOR_OPTIONS_FRAGMENT = gql`
	fragment ColorOptionsFragment on ColorOption {
		colorOptionId
		name
		description
		position
		type
		colorPalettes {
			name
			description
			upcharge
			colors {
				label
				description
				hexCode
				isVisible
				colorId
				isRetired
				isCustomColor
			}
		}
	}
`;

export const GET_ANY_ITEM_BY_ID = gql`
	${SIZE_CHART_FRAGMENT}
	${COLOR_OPTIONS_FRAGMENT}
	query GetAnyItem($id: Int!) {
		inventoryItem: anyInventoryItemById(id: $id) {
			itemId: inventoryItemId
			itemType: type
			styleCode
			description
			vendorCost
			price
			systemCategoryId
			dropShip
			processTypeId
			colorOptions {
				...ColorOptionsFragment
			}
			... on InventoryItem {
				sizeChart {
					...SizeChartFragment
				}
			}
		}
	}
`;

/**
 * Color options + size chart for a sales line item's inventory record, regardless of whether
 * that record is an Item, SubItem or Package. Resolves by id through anyInventoryItemById so
 * "system" rows whose Type discriminator disagrees with their system category (VIP, credits,
 * the draft placeholder) still resolve instead of returning null (#1472 follow-up).
 */
export const GET_ITEM_SIZE_COLOR_DATA = gql`
	${COLOR_OPTIONS_FRAGMENT}
	query GetItemSizeColorData($id: Int!) {
		inventoryItem: anyInventoryItemById(id: $id) {
			itemType: type
			colorOptions {
				...ColorOptionsFragment
			}
			... on InventoryItem {
				sizeChart {
					name
					description
					includeLengths
					sizes {
						lengths {
							length
							sizeId
							sizeLengthId
							isRetired
						}
						name
						showSize
						sizeId
						isRetired
						sequence
					}
				}
			}
		}
	}
`;

export const GET_INVENTORY_COLOR_OPTIONS_IDS = gql`
	query GetInventoryColors($where: InventoryBaseItemFilterInput) {
		response: allInventoryItems(where: $where) {
			items {
				inventoryItemId
				description
				colorOptions {
					colorOptionId
					name
					description
					type
					position
					colorPalettes {
						name
						description
						colors {
							label
							description
							hexCode
							isVisible
							colorId
							isRetired
						}
					}
				}
			}
		}
	}
`;

export const GET_INVENTORY_ITEMS_SUPPLIER = gql`
	query GetInventoryItems($offset: Int = 0, $take: Int = 500, $where: InventoryItemFilterInput) {
		response: inventoryItems(
			skip: $offset
			take: $take
			where: $where
			order: { createDate: DESC }
		) {
			items {
				inventoryItemId
				styleCode
				description
				leadTime
				vendorCost
				vendorId
				vendorPartNumber
				vendor {
					vendorId
					companyName
				}
				suppliers {
					vendorId
					leadTimeInWeeks
					vendorPartNumber
					cost
					vendor {
						companyName
					}
				}
				inactiveDate
				createDate
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			totalCount
		}
	}
`;

export const GET_ALL_INVENTORY_ITEMS_SUPPLIER = gql`
	query GetAllInventoryItems(
		$offset: Int = 0
		$take: Int = 500
		$where: InventoryBaseItemFilterInput
	) {
		response: allInventoryItems(
			skip: $offset
			take: $take
			where: $where
			order: { createDate: DESC }
		) {
			items {
				inventoryItemId
				styleCode
				description
				leadTime
				vendorCost
				vendorId
				vendorPartNumber
				vendor {
					vendorId
					companyName
				}
				suppliers {
					vendorId
					leadTimeInWeeks
					vendorPartNumber
					cost
					vendor {
						companyName
					}
				}
				inactiveDate
				createDate
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			totalCount
		}
	}
`;

export const GET_INVENTORY_ITEMS = gql`
	query GetInventoryItemsAvailable(
		$offset: Int = 0
		$take: Int = 500
		$where: InventoryItemFilterInput
	) {
		response: inventoryItems(
			skip: $offset
			take: $take
			where: $where
			order: { createDate: DESC }
		) {
			items {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				infiniteInventory
				inventoryInfo {
					binNumber
					onHand
					committed
				}
				sizeChart {
					sizes {
						name
						inventoryInfo {
							binNumber
							onHand
							committed
						}
						isRetired
					}
				}
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			totalCount
		}
	}
`;

export const GET_ALL_INVENTORY_ITEMS = gql`
	query GetAllInventoryItemsAvailable(
		$offset: Int = 0
		$take: Int = 20
		$where: InventoryBaseItemFilterInput
		$order: [InventoryBaseItemSortInput!] = [{ styleCode: ASC }]
	) {
		response: allInventoryItems(
			skip: $offset
			take: $take
			where: $where
			order: $order
		) {
			items {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				infiniteInventory
				inventoryInfo {
					inventoryItemId
					binNumber
					onHand
					committed
					need
					onOrder
					infiniteInventory
				}
				sizeChart {
					sizes {
						name
						inventoryInfo {
							sizeId
							inventoryItemId
							binNumber
							onHand
							committed
							need
							onOrder
						}
						isRetired
						showSize
					}
				}
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			totalCount
		}
	}
`;

export const PACKAGE_CATEGORY_QUERY = gql`
	query packageCategoryById($id: Int!) {
		packageCategoryById(id: $id) {
			packageCategoryId
			name
			description
			systemCategoryId
			inventoryItemId
			inventoryItem {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				type
				processTypeId
			}
			items {
				inventoryItemId
				inactiveDate
				systemCategoryId
				styleCode
				description
				processTypeId
			}
		}
	}
`;

export const SUB_ITEM_CATEGORY_QUERY = gql`
	query subItemCategoryById($id: Int!) {
		subItemCategoryById(id: $id) {
			subItemCategoryId
			name
			description
			systemCategoryId
			subItems {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				processTypeId
				inactiveDate
			}
		}
	}
`;

export const SEARCH_INVENTORY_LISTINGS_BY_SYSTEMCATEGORYID_QUERY = gql`
	query SearchInventoryListing(
		$searchTerm: String
		$systemCategoryId: Int!
		$offset: Int = 0
		$take: Int = 20
	) {
		inventoryListings(
			searchTerm: $searchTerm
			skip: $offset
			take: $take
			order: { styleCode: ASC }
			where: { inactiveDate: { eq: null }, systemCategoryId: { eq: $systemCategoryId } }
		) {
			items {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				type
				processTypeId
			}
		}
	}
`;

export const SEARCH_INVENTORY_LISTINGS_QUERY = gql`
	query SearchInventoryListing(
		$searchTerm: String
		$itemTypes: [String!]
		$offset: Int = 0
		$take: Int = 20
	) {
		inventoryListings(
			searchTerm: $searchTerm
			skip: $offset
			take: $take
			order: { styleCode: ASC }
			where: { inactiveDate: { eq: null }, type: { in: $itemTypes } }
		) {
			items {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				type
				processTypeId
			}
		}
	}
`;

// Available Inventory page query - uses flat InventoryListing view for sortable quantity columns
export const GET_INVENTORY_LISTINGS = gql`
	query GetInventoryListings(
		$offset: Int = 0
		$take: Int = 20
		$searchTerm: String
		$where: InventoryItemListingFilterInput
		$order: [InventoryItemListingSortInput!] = [{ styleCode: ASC }]
	) {
		response: inventoryListings(
			searchTerm: $searchTerm
			skip: $offset
			take: $take
			where: $where
			order: $order
		) {
			items {
				inventoryItemId
				systemCategoryId
				styleCode
				description
				stockItems
				infiniteInventory
				available
				onHand
				committed
				need
				onOrder
				backorder
				hasSizes
				binNumber
				inactiveDate
				restockQuantity
				restockStatus
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
			}
			totalCount
		}
	}
`;

// Size inventory info for expanded rows - provides per-size inventory data
// Note: The view includes ALL sizes; filter by showSize client-side to match original behavior
// Sort by sequence to maintain size chart order (not alphabetical)
export const GET_SIZE_INVENTORY_INFO = gql`
	query GetSizeInventoryInfo(
		$where: SizeInventoryInfoFilterInput
		$order: [SizeInventoryInfoSortInput!] = [{ sequence: ASC }]
	) {
		response: sizeInventoryInfo(take: 500, where: $where, order: $order) {
			items {
				inventoryItemId
				sizeId
				name
				showSize
				isRetired
				sequence
				binNumber
				onHand
				committed
				need
				onOrder
				backorder
				restockQuantity
				restockStatus
			}
		}
	}
`;

export const GET_INVENTORY_SETTINGS = gql`
	query GetInventorySettings {
		inventorySettings {
			id
			nearRestockBuffer
		}
	}
`;
